import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useWorkspaceStore, type Workspace, type PostalAddress } from '../../stores/workspaceStore'
import { workspaceService } from '../../services/workspaceService'
import { workspacesApi, oauthApi, webhooksApi, governanceApi } from '../../services/api'
import ConfigPanel from './ConfigPanel'
import ProductPolicyCard from './ProductPolicyCard'
import { FACT_LIMITS, catalogueView, emailCapSource, suitabilityLive, typeEffects, type EmailCapSource } from './storeSections'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  ChipGroup,
  Code,
  CodeBlock,
  ConfirmDialog,
  EmptyValue,
  Eyebrow,
  Field,
  FieldGrid,
  FieldLabel,
  Hint,
  Inline,
  KeyValue,
  LoadingState,
  Meter,
  SettingRow,
  Spacer,
  Stack,
  Stat,
  TileGrid,
} from '../../ds'

/**
 * Store — the facts about this shop, what Marva worked out from its catalogue, and the Shopify
 * connection underneath both. Laid out to v6 (MAR-386): four sections, one open at a time, chosen
 * by `StoreTab`'s section nav. Each section reads the workspace itself and is keyed by its id, so
 * a workspace switch remounts every draft rather than painting the last store's for a frame.
 *
 * ⚠️ **No store fact claims to have been read, observed or inferred.** The v5 design showed each
 * fact as a derived belief with a "Re-read the site" action; **no such deriver exists**
 * (`docs/work/app-ia-v5.md` §3). The one thing on this tab allowed to say "inferred" is the store
 * type: `storeDomain.archetype` genuinely is classified by the store-classifier and carries its
 * own confidence. "You set these" on the facts is true — the merchant's PUT is their only writer
 * on a real store. ⚠️ Not on a [SIM] tenant: `tools/simulator/scripts/seed-synthetic-workspace.js`
 * seeds `storeContext` (and a `source: 'merchant'` postal address), so on a synthetic workspace
 * "You set these" / "You set this" describe the seed script, not a merchant.
 *
 * Where the v6 design is overruled (`docs/history/work/app-ia-finish.md` §3):
 *  - "What being a <type> changes" quotes no SMS default. The preset's `smsPerMonth` is not
 *    enforced — `resolveEffectiveCaps` falls back to a fixed cap for texts, whatever the type.
 *  - Shopper archetypes and the sale-hunter / full-price thresholds are not drawn, and the
 *    lifecycle tiers stay the quiet key-value rows they were (`deliberately-not-doing.md`:
 *    segments are derived opinions, not something the surface promotes).
 *  - "What she pays most attention to" is not drawn: that text lives in the observation
 *    extractor's prompt, which the browser cannot import, and a copy here would drift from it.
 *  - The tracking-script chip says "App installed", never that the script is on the storefront:
 *    `shopify.isInstalled` is all that is known.
 *  - "What she watches for" lists only signals with a live reader: style preference and project
 *    tracking feed nothing but the suitability packs, so they appear only once suitability
 *    capture is live (`suitabilityLive`).
 */

const ARCHETYPE_NAMES: Record<string, string> = {
  fashion_boutique: 'Fashion Boutique',
  beauty_wellness: 'Beauty & Wellness',
  home_decor: 'Home & Decor',
  electronics_tech: 'Electronics & Tech',
  gift_specialty: 'Gift & Specialty',
  luxury_premium: 'Luxury & Premium',
  consumables: 'Consumables',
  general_retail: 'General Retail',
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

/** The workspace on screen, or the plain "nothing selected" card — shared by every section. */
function useSectionWorkspace() {
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore()
  return { workspace: currentWorkspace as Workspace | null, setCurrentWorkspace }
}

const NoWorkspace = () => (
  <Card>
    <CardBody>No workspace selected.</CardBody>
  </Card>
)

/* ── Store type ─────────────────────────────────────────────────────────────── */

/**
 * The stored email caps, so the email row can say whether the type's default is in force. Read
 * once per store (this section is keyed by workspace id, so a switch remounts it) and stamped
 * with the store it is for; a failed or pending read is `unknown`, and the row then says neither.
 */
function useEmailCapSource(workspaceId: string): EmailCapSource {
  const [read, setRead] = useState<{ workspaceId: string; source: EmailCapSource } | null>(null)
  useEffect(() => {
    let live = true
    governanceApi
      .getEngagement(workspaceId)
      .then((res) => {
        if (!live) return
        // An empty body is "no rules stored", which is the default — not a failure.
        const email = res.success ? res.data?.frequencyCaps?.email : undefined
        setRead({
          workspaceId,
          source: emailCapSource(res.success ? { ok: true, perWeek: email?.perWeek, perMonth: email?.perMonth } : { ok: false }),
        })
      })
      .catch(() => {
        if (live) setRead({ workspaceId, source: { kind: 'unknown' } })
      })
    return () => {
      live = false
    }
  }, [workspaceId])
  return read && read.workspaceId === workspaceId ? read.source : { kind: 'unknown' }
}

const CAPS_LINK = '/authority?tab=limits&section=contact'

/** One line under the email default: is it the cap in force, or are the merchant's own? */
function EmailCapHint({ source }: { source: EmailCapSource }) {
  const limits = <Link to={CAPS_LINK}>Limits</Link>
  if (source.kind === 'default') {
    return <Hint data-testid="email-cap-source">In force: you have no email cap of your own. Setting one in {limits} overrides this.</Hint>
  }
  if (source.kind === 'own') {
    const own = [
      source.perWeek !== null && `${source.perWeek} a week`,
      source.perMonth !== null && `${source.perMonth} a month`,
    ].filter(Boolean).join(' and ')
    // The enforcer picks each period independently, so a one-sided cap replaces only its own
    // period. Name the period the default still sets — "covers the rest" read as contradicting
    // the number directly above it.
    const rest = source.perWeek === null ? 'weekly' : source.perMonth === null ? 'monthly' : null
    return (
      <Hint data-testid="email-cap-source">
        {rest
          ? <>Your own cap in {limits} applies ({own}); the {rest} default above still applies.</>
          : <>Your own caps in {limits} apply ({own}) instead of these defaults.</>}
      </Hint>
    )
  }
  return <Hint data-testid="email-cap-source">The type&apos;s default, unless you set your own in {limits}.</Hint>
}

const withArticle = (name: string) => `${/^[AEIOU]/i.test(name) ? 'an' : 'a'} ${name}`

function StoreType({
  workspace,
  setCurrentWorkspace,
}: {
  workspace: Workspace
  setCurrentWorkspace: (w: Workspace) => void
}) {
  const [savingType, setSavingType] = useState(false)
  const [typeError, setTypeError] = useState('')
  const sd = workspace.storeDomain
  const capSource = useEmailCapSource(workspace.id)

  // ⚠️ Optimistic, so the rollback is not optional. `try/finally` with no `catch` left a
  // refused or thrown write showing the merchant's correction as applied — until the next
  // load silently put it back.
  const overrideType = async (archetype: string) => {
    if (!sd || archetype === sd.archetype) return
    const nextStoreDomain = { ...sd, archetype, merchantOverride: true, classifiedBy: 'merchant' as const }
    const previous = workspace
    setCurrentWorkspace({ ...workspace, storeDomain: nextStoreDomain })
    setSavingType(true)
    setTypeError('')
    try {
      const response = await workspacesApi.update(workspace.id, { storeDomain: nextStoreDomain } as Parameters<typeof workspacesApi.update>[1])
      // A refusal is a 200-shaped `{ success: false }`, not a throw — both have to revert.
      if (!response.success) throw new Error(response.error || 'the server refused it.')
    } catch (e) {
      setCurrentWorkspace(previous)
      setTypeError(`Your store type was not changed: ${e instanceof Error ? e.message : 'the save failed.'}`)
    } finally {
      setSavingType(false)
    }
  }

  if (!sd?.archetype) {
    return (
      <Card>
        <CardHeader title="Store type" />
        <CardBody>
          Not yet classified — Marva classifies your store from your catalogue and orders once it has enough signal.
        </CardBody>
      </Card>
    )
  }

  const typeName = ARCHETYPE_NAMES[sd.archetype] || sd.archetype
  const confidence = typeof sd.confidence === 'number' ? Math.round(sd.confidence * 100) : null
  const catalogue = catalogueView(workspace)
  const effects = typeEffects(sd.archetype, workspace.shopify?.currency, suitabilityLive(workspace))
  const effectsTitle = `What being ${withArticle(typeName)} changes`

  return (
    <Stack>
      <Card data-testid="store-type-card">
        <Stack gap="sm">
          <Eyebrow>Store type · {sd.merchantOverride ? 'your override' : 'Marva inferred'}</Eyebrow>
          <CardHeader
            titleAs="h2"
            title={typeName}
            description={
              sd.merchantOverride
                ? 'You set this type. It shapes how she acts — change it if it no longer sounds like you.'
                : <>What Marva inferred about your store from your catalogue, orders and how your customers behave. It shapes how she acts — override the type if it doesn&apos;t sound like you.</>
            }
          />
          {confidence !== null && !sd.merchantOverride && (
            <Meter
              label="Confidence"
              value={confidence}
              valueText={`${confidence}% confidence`}
              layout="inline"
              size="sm"
            />
          )}
          {sd.classifiedAt && <Hint>Last classified · {shortDate(sd.classifiedAt)}</Hint>}
          <Field
            as="select"
            label="Override store type"
            value={sd.archetype}
            disabled={savingType}
            hint={`Changing it changes what is in \u201c${effectsTitle}\u201d.`}
            onChange={(e) => overrideType((e.target as HTMLSelectElement).value)}
          >
            {Object.entries(ARCHETYPE_NAMES).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Field>
          {typeError && <Alert tone="danger" live="assertive">{typeError}</Alert>}
        </Stack>
      </Card>

      <Card data-testid="catalogue-card">
        <CardHeader
          title="What she read in your catalogue"
          aside={catalogue.measured ? undefined : <Hint>Not measured yet</Hint>}
        />
        <Stack gap="sm">
          {catalogue.categories.length > 0 && (
            <Stack gap="sm">
              {catalogue.categories.map((c) => (
                <Meter key={c.key} label={c.label} value={c.percent} valueText={`${c.percent}%`} layout="inline" size="sm" />
              ))}
            </Stack>
          )}
          <TileGrid size="sm">
            <Stat tone="gold" label="Average price" value={catalogue.avgPrice ?? <EmptyValue />} />
            <Stat label="Products" value={catalogue.productCount ?? <EmptyValue />} />
            <Stat label="Sized products" value={catalogue.sized ?? <EmptyValue />} />
            <Stat label="Consumables" value={catalogue.consumables ?? <EmptyValue />} />
          </TileGrid>
          <Hint>
            {catalogue.measured
              ? 'The largest share of your catalogue usually decides the type; a high average price can make it Luxury & Premium.'
              : 'Nothing has been measured yet. Marva reads your catalogue once it has products to read.'}
          </Hint>
        </Stack>
      </Card>

      <Card data-testid="type-effects-card">
        <CardHeader
          title={effectsTitle}
          description="She reads your customers with these. They come with the type."
        />
        <Stack>
          <Stack gap="sm">
            <FieldLabel>What she watches for</FieldLabel>
            <ChipGroup label="What she watches for">
              {effects.signals.map((s) =>
                s.on ? (
                  <Chip key={s.key} tone="sage" dot>{s.label}</Chip>
                ) : (
                  <Chip key={s.key}>{s.label} · off</Chip>
                )
              )}
            </ChipGroup>
          </Stack>
          <TileGrid size="sm">
            {/* One number per period: "3 / 10" under a slash-joined label had to be decoded. */}
            <Stat label="Emails a week · type default" value={effects.emailPerWeek} />
            <Stat label="Emails a month · type default" value={effects.emailPerMonth} />
          </TileGrid>
          <EmailCapHint source={capSource} />
          <KeyValue
            columns={2}
            items={[
              { label: 'VIP', value: effects.vip },
              { label: 'At risk / churned', value: `after ${effects.atRiskDays} / ${effects.churnedDays} days without an order` },
            ]}
          />
        </Stack>
      </Card>
    </Stack>
  )
}

export function StoreTypeSection() {
  const { workspace, setCurrentWorkspace } = useSectionWorkspace()
  if (!workspace) return <NoWorkspace />
  return <StoreType key={workspace.id} workspace={workspace} setCurrentWorkspace={setCurrentWorkspace} />
}

/* ── What she may say ───────────────────────────────────────────────────────────
   Standing store facts ([STORE CONTEXT]) — what Marva may state on the store's authority when
   customers ask shipping/returns/support questions. Without these she declines and offers to
   pass the question along. */

const FACT_KEYS = ['shipping', 'returns', 'supportContact', 'notes'] as const

function StoreFactsCard({
  workspace,
  setCurrentWorkspace,
}: {
  workspace: Workspace
  setCurrentWorkspace: (w: Workspace) => void
}) {
  const [facts, setFacts] = useState({
    shipping: workspace.storeContext?.shipping || '',
    returns: workspace.storeContext?.returns || '',
    supportContact: workspace.storeContext?.supportContact || '',
    notes: workspace.storeContext?.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const stored = workspace.storeContext || {}
  const changed = FACT_KEYS.some((k) => facts[k].trim() !== (stored[k] || ''))
  const anyStored = FACT_KEYS.some((k) => (stored[k] || '').trim())

  // ⚠️ The mirror happens only once the server has agreed. Previously a throw skipped it
  // (so the form and the store diverged with no message) and a `{ success: false }`
  // refusal sailed past it (so a refused write was mirrored as fact).
  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const storeContext = Object.fromEntries(
        // The server's own cut (trim, then FACT_LIMITS), so the mirror is what it stored.
        FACT_KEYS.map((k) => [k, facts[k].trim().slice(0, FACT_LIMITS[k])]).filter(([, v]) => v)
      )
      const response = await workspacesApi.update(workspace.id, { storeContext } as Parameters<typeof workspacesApi.update>[1])
      if (!response.success) throw new Error(response.error || 'the server refused it.')
      // The merchant may have switched workspace while this was in flight: mirroring the
      // closed-over workspace would switch them back. The write itself landed on its own id.
      if (useWorkspaceStore.getState().currentWorkspace?.id !== workspace.id) return
      setCurrentWorkspace({ ...workspace, storeContext })
    } catch (e) {
      setError(`Your store facts were not saved: ${e instanceof Error ? e.message : 'the save failed.'}`)
    } finally {
      setSaving(false)
    }
  }

  const field = (key: keyof typeof facts, label: string, placeholder: string) => (
    <Field
      as="textarea"
      label={label}
      value={facts[key]}
      onChange={(e) => setFacts({ ...facts, [key]: (e.target as HTMLTextAreaElement).value })}
      placeholder={placeholder}
      maxLength={FACT_LIMITS[key]}
    />
  )

  return (
    <Card data-testid="store-facts-card">
      <CardHeader
        title="Store facts"
        description={
          <>
            What Marva may tell customers on your authority — shipping, returns and how to reach you. Anything you leave
            blank she won&apos;t guess at: she&apos;ll offer to pass the question to you instead.
          </>
        }
      />
      <Stack gap="sm">
        <FieldGrid>
          {field('shipping', 'Shipping', 'e.g. 3–5 business days within the UK, €8 flat to Ireland (5–7 days)')}
          {field('returns', 'Returns', 'e.g. 30-day returns on unworn items, free return label included')}
          {field('supportContact', 'Support contact', 'e.g. help@yourstore.com')}
          {field('notes', 'Anything else', 'e.g. gift wrapping available at checkout')}
        </FieldGrid>
        {error && <Alert tone="danger" live="assertive">{error}</Alert>}
      </Stack>
      <CardFooter>
        {/* True: the merchant's workspace PUT is the only writer of `storeContext`. Shown only
            once something is stored — "you set these" over four blanks would be a claim about nothing. */}
        {anyStored && <Chip data-testid="store-facts-source">You set these</Chip>}
        <Spacer />
        <Button size="sm" busy={saving} busyLabel="Saving…" disabled={!changed} onClick={save}>
          Save changes
        </Button>
      </CardFooter>
    </Card>
  )
}

export function StoreFactsSection() {
  const { workspace, setCurrentWorkspace } = useSectionWorkspace()
  if (!workspace) return <NoWorkspace />
  return <StoreFactsCard key={workspace.id} workspace={workspace} setCurrentWorkspace={setCurrentWorkspace} />
}

/* ── Postal address ───────────────────────────────────────────────────────────── */

/* The shop's postal address (MAR-330). Commercial email must carry the sender's
   physical address, so every email to a shopper prints this one in its footer — and
   with none stored, those emails hold. Seeded from the address in the merchant's
   Shopify store settings at install; once the merchant saves it, it is theirs and a
   reinstall leaves it alone.

   The provenance line is a fact about which door the value came through (the server
   stamps `source`), not an inference — the one thing this tab may not claim. */
const ADDRESS_FIELDS = ['address1', 'address2', 'city', 'province', 'zip', 'country'] as const
type AddressField = typeof ADDRESS_FIELDS[number]
type AddressForm = Record<AddressField, string>

function addressForm(address: PostalAddress | undefined): AddressForm {
  return {
    address1: address?.address1 || '',
    address2: address?.address2 || '',
    city: address?.city || '',
    province: address?.province || '',
    zip: address?.zip || '',
    country: address?.country || '',
  }
}

function PostalAddressCard({
  workspace,
  setCurrentWorkspace,
}: {
  workspace: Workspace
  setCurrentWorkspace: (w: Workspace) => void
}) {
  const stored = workspace.postalAddress
  const [form, setForm] = useState<AddressForm>(() => addressForm(stored))
  const [saving, setSaving] = useState(false)
  // No re-sync effect: PostalAddressSection keys this card by workspace id, so a workspace switch
  // remounts it and the initialisers above start clean. A save re-syncs `form` itself.
  const [error, setError] = useState('')

  const storedForm = addressForm(stored)
  const changed = ADDRESS_FIELDS.some(k => form[k].trim() !== storedForm[k])
  const empty = ADDRESS_FIELDS.every(k => !form[k].trim())

  const save = async () => {
    setError('')
    const trimmed = Object.fromEntries(
      ADDRESS_FIELDS.map(k => [k, form[k].trim()]).filter(([, v]) => v)
    ) as Partial<AddressForm>
    if (!empty && !(trimmed.address1 && trimmed.city && trimmed.country)) {
      setError('Your address was not saved: address line 1, city and country are all needed.')
      return
    }
    // Every field cleared is the merchant removing the address; `null` is the server's
    // "remove" (and the emails then hold, which the empty-state copy says).
    // A Shopify country code only stays while the country it belongs to does.
    const keepCode = stored?.countryCode && trimmed.country === stored.country
    const postalAddress = empty ? null : { ...trimmed, ...(keepCode && { countryCode: stored?.countryCode }) }
    setSaving(true)
    try {
      const response = await workspacesApi.update(workspace.id, { postalAddress } as Parameters<typeof workspacesApi.update>[1])
      if (!response.success) throw new Error(response.error || 'the server refused it.')
      // Mirror only what the server agreed to. It stamps `source` and `updatedAt`; prefer
      // its copy, and fall back to the same stamp it applies.
      const echoed = (response.data as { postalAddress?: PostalAddress } | undefined)?.postalAddress
      const next: PostalAddress | undefined = postalAddress === null
        ? undefined
        : echoed || { ...(postalAddress as PostalAddress), source: 'merchant', updatedAt: new Date().toISOString() }
      // Same guard as the Store facts save: a late answer must not undo a workspace switch.
      if (useWorkspaceStore.getState().currentWorkspace?.id !== workspace.id) return
      setCurrentWorkspace({ ...workspace, postalAddress: next })
      setForm(addressForm(next))
    } catch (e) {
      setError(`Your address was not saved: ${e instanceof Error ? e.message : 'the save failed.'}`)
    } finally {
      setSaving(false)
    }
  }

  const field = (key: AddressField, label: string, placeholder: string) => (
    <Field
      label={label}
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: (e.target as HTMLInputElement).value })}
      placeholder={placeholder}
    />
  )

  return (
    <Card data-testid="postal-address-card">
      <CardHeader
        title="Postal address"
        description="Printed at the foot of the emails Marva sends to your shoppers. Commercial email has to carry a physical postal address, so it needs to be one where your business can be reached by post."
      />
      <Stack gap="sm">
        {stored ? (
          <Hint data-testid="postal-address-source">
            {stored.source === 'shopify'
              ? 'Copied from the address in your Shopify store settings. Edit it here if it is wrong; your version will be kept.'
              : 'You set this. Reconnecting Shopify will not change it.'}
          </Hint>
        ) : (
          <Alert tone="attention" live="polite" data-testid="postal-address-missing">
            Emails Marva starts with your shoppers are on hold until you add an address, because commercial email
            must carry one. Replies to shoppers who wrote to you still go out.
          </Alert>
        )}
        <FieldGrid>
          {field('address1', 'Address line 1', 'e.g. 12 High Street')}
          {field('address2', 'Address line 2', 'e.g. Unit 4 (optional)')}
          {field('city', 'City', 'e.g. Edinburgh')}
          {field('province', 'State / county', 'Optional')}
          {field('zip', 'Postcode / ZIP', 'e.g. EH1 1AA')}
          {field('country', 'Country', 'e.g. United Kingdom')}
        </FieldGrid>
        {error && <Alert tone="danger" live="assertive">{error}</Alert>}
      </Stack>
      <CardFooter>
        <Spacer />
        <Button size="sm" busy={saving} busyLabel="Saving…" disabled={!changed} onClick={save}>
          Save address
        </Button>
      </CardFooter>
    </Card>
  )
}

export function PostalAddressSection() {
  const { workspace, setCurrentWorkspace } = useSectionWorkspace()
  if (!workspace) return <NoWorkspace />
  return <PostalAddressCard key={workspace.id} workspace={workspace} setCurrentWorkspace={setCurrentWorkspace} />
}

/* ── Shopify connection ─────────────────────────────────────────────────────────
   ONE card: the store's name, the connection, and the connection's own operations (data &
   webhooks, the storefront script) as rows inside it. They were three cards with the store's
   name and URL drawn twice; v6 merges them. The Manage / View script drawers and the
   first-connect input are unchanged (MAR-318 designs only half of them). */

type StoreDrawer = 'shopify' | 'tracking'

function ShopifyConnectionCard({ workspace }: { workspace: Workspace }) {
  const [name, setName] = useState(workspace.name || '')
  const [savingName, setSavingName] = useState(false)
  const [nameError, setNameError] = useState('')
  const [reconnecting, setReconnecting] = useState(false)
  const [reconnectError, setReconnectError] = useState('')
  // First-time connect on a workspace with no stored shop domain (a workspace
  // created outside onboarding has no other path to Shopify).
  const [shopInput, setShopInput] = useState('')
  const [drawer, setDrawer] = useState<StoreDrawer | null>(null)

  const shopify = workspace.shopify
  const storeDomain = shopify?.shop || shopify?.storeUrl
  const isConnected = !!(shopify?.isInstalled || shopify?.connected)
  // ⚠️ The only thing known about the storefront script is that the APP is installed — nothing
  // reports the snippet as present on, or heard from, the storefront. Worded as exactly that.
  const appInstalled = shopify?.isInstalled === true
  const webhooksActive = workspace.webhooks?.status === 'connected'
  const nameChanged = name.trim() !== '' && name.trim() !== workspace.name

  // Connect/Reconnect runs the same OAuth install flow as onboarding. Reconnect is the
  // recovery path for a Shopify-side uninstall (which revokes the token while we may still
  // show Connected) and the re-grant path when the app's requested scopes grow; first-time
  // connect uses the typed shop domain instead of the stored one.
  const reconnectShopify = async () => {
    const shopDomain = storeDomain || shopInput.trim()
    if (!shopDomain) return
    setReconnecting(true)
    setReconnectError('')
    try {
      const normalized = shopDomain.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
      const withSuffix = normalized.includes('.') ? normalized : `${normalized}.myshopify.com`
      const res = await oauthApi.getConnectUrl(withSuffix, workspace.id)
      if (res.success && res.data?.authorizationUrl) {
        window.location.href = res.data.authorizationUrl
      } else {
        setReconnectError(res.error || 'Failed to start the Shopify connection.')
        setReconnecting(false)
      }
    } catch (e) {
      setReconnectError(e instanceof Error ? e.message : 'Failed to connect Shopify.')
      setReconnecting(false)
    }
  }

  const saveName = async () => {
    setSavingName(true)
    setNameError('')
    try {
      await workspaceService.updateWorkspaceName(workspace.id, name.trim())
    } catch (e) {
      // No catch here meant "Saving…" simply stopped and the merchant read that as saved.
      setNameError(`Your workspace name was not saved: ${e instanceof Error ? e.message : 'the save failed.'}`)
    } finally {
      setSavingName(false)
    }
  }

  return (
    <>
      <Card data-testid="shopify-card">
        <CardHeader
          title="Shopify connection"
          description="This Shopify store and how it's named across Marva."
          aside={isConnected ? <Badge tone="sage" dot>Connected</Badge> : <Badge dot>Not connected</Badge>}
        />
        <Stack gap="sm">
          <FieldGrid>
            <Field label="Workspace name" value={name} onChange={(e) => setName((e.target as HTMLInputElement).value)} placeholder="Workspace name" />
            {storeDomain && (
              <Field
                label="Store URL"
                value={storeDomain}
                readOnly
                // `installedAt` is rewritten on every OAuth grant (oauth-handler), reconnects included — so
                // it is when Marva was last authorised, not when the store first connected.
                hint={isConnected && shopify?.installedAt ? `Authorised ${shortDate(shopify.installedAt)}` : undefined}
              />
            )}
          </FieldGrid>
          {!storeDomain && (
            <Inline align="end" grow>
              <Field
                value={shopInput}
                onChange={(e) => setShopInput((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && shopInput.trim() && !reconnecting) reconnectShopify() }}
                placeholder="your-store.myshopify.com"
                aria-label="Shopify store domain"
                type="url"
              />
              <Button variant="ghost" size="sm" busy={reconnecting} busyLabel="Redirecting…" disabled={!shopInput.trim()} onClick={reconnectShopify}>
                Connect
              </Button>
            </Inline>
          )}
          <SettingRow
            data-testid="row-webhooks"
            title="Data & webhooks"
            description="Re-pull your catalogue and orders, or reconnect the real-time updates."
            aside={
              <>
                {/* ⚠️ This reports a flag THIS UI writes (`toggleWebhooks`), not whether Shopify is
                    actually delivering. Worded as a registration, not as a live state. */}
                {webhooksActive ? <Badge tone="sage" dot>Registered</Badge> : <Badge dot>Not registered</Badge>}
                {/* ⚠️ Never disabled. A Shopify-side uninstall revokes the token while
                    `webhooks.status` stays 'connected'; gating this on the connection would strand
                    the merchant outside the one panel that clears the stale registration. */}
                <Button variant="ghost" size="sm" onClick={() => setDrawer('shopify')}>Manage</Button>
              </>
            }
          />
          <SettingRow
            data-testid="row-tracking"
            title="Tracking script"
            description="The storefront snippet that feeds the behavioural stream."
            aside={
              <>
                {appInstalled ? <Badge tone="sage" dot>App installed</Badge> : <Badge dot>App not installed</Badge>}
                <Button variant="ghost" size="sm" onClick={() => setDrawer('tracking')}>View script</Button>
              </>
            }
          />
          {reconnectError && <Alert tone="danger" live="assertive">{reconnectError}</Alert>}
          {nameError && <Alert tone="danger" live="assertive">{nameError}</Alert>}
        </Stack>
        <CardFooter>
          <Hint>
            {storeDomain
              ? 'Reconnecting re-authorises Marva with Shopify, upgrades permissions and re-registers all webhooks automatically.'
              : 'Connecting authorises Marva with Shopify and registers all webhooks automatically.'}
          </Hint>
          <Spacer />
          {storeDomain && (
            <Button variant="ghost" size="sm" busy={reconnecting} busyLabel="Redirecting…" onClick={reconnectShopify}>
              {isConnected ? 'Reconnect' : 'Connect'}
            </Button>
          )}
          {/* Named for the one thing it saves: beside Reconnect, "Save changes" read as saving the
              connection too. */}
          <Button size="sm" busy={savingName} busyLabel="Saving…" disabled={!nameChanged} onClick={saveName}>
            Save name
          </Button>
        </CardFooter>
      </Card>

      <ConfigPanel title="Shopify" isOpen={drawer === 'shopify'} onClose={() => setDrawer(null)}>
        <ShopifyManagePanel workspace={workspace} />
      </ConfigPanel>
      <ConfigPanel title="Tracking script" isOpen={drawer === 'tracking'} onClose={() => setDrawer(null)}>
        <TrackingScriptPanel workspace={workspace} />
      </ConfigPanel>
    </>
  )
}

export function ShopifySection() {
  const { workspace } = useSectionWorkspace()
  if (!workspace) return <NoWorkspace />
  return (
    <Stack>
      <ShopifyConnectionCard key={workspace.id} workspace={workspace} />
      {/* Reset save/busy state when the merchant changes workspace. */}
      <ProductPolicyCard key={`policy-${workspace.id}`} />
    </Stack>
  )
}

// Shopify management — sync catalogue/orders and connect/disconnect webhooks.
function ShopifyManagePanel({ workspace }: { workspace: Workspace | null }) {
  const { setCurrentWorkspace } = useWorkspaceStore()
  const [syncing, setSyncing] = useState(false)
  const [webhookBusy, setWebhookBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)
  if (!workspace) return null

  const shopify = workspace.shopify
  const connected = !!(shopify?.isInstalled || shopify?.connected)
  const webhookStatus = workspace.webhooks?.status === 'connected' ? 'connected' : 'disconnected'

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 3000) }

  const syncData = async () => {
    setSyncing(true)
    try {
      const r = await workspaceService.syncData(workspace.id)
      flash((r as { message?: string })?.message || 'Data sync started.')
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Sync failed.')
    } finally {
      setSyncing(false)
    }
  }

  const toggleWebhooks = async () => {
    setWebhookBusy(true)
    try {
      if (webhookStatus === 'disconnected') {
        await workspaceService.testWebhooks(workspace.id)
        await workspaceService.updateWorkspaceWebhookStatus(workspace.id, 'connected')
        setCurrentWorkspace({ ...workspace, webhooks: { ...workspace.webhooks, status: 'connected' } })
        flash('Webhooks connected.')
      } else {
        const r = await webhooksApi.setup({ accountId: workspace.accountId, workspaceId: workspace.id, action: 'delete' })
        if (!r.success) throw new Error(r.error || 'Failed to disconnect webhooks')
        await workspaceService.updateWorkspaceWebhookStatus(workspace.id, 'disconnected')
        setCurrentWorkspace({ ...workspace, webhooks: { ...workspace.webhooks, status: 'disconnected' } })
        flash('Webhooks disconnected.')
      }
    } catch (e) {
      flash(e instanceof Error ? e.message : 'Operation failed.')
    } finally {
      setWebhookBusy(false)
    }
  }

  return (
    <Stack>
      {msg && <Alert tone="attention" live="polite">{msg}</Alert>}

      <Card>
        <CardHeader
          title="Store"
          description={shopify?.storeUrl || shopify?.shop || 'No store connected'}
          aside={connected ? <Badge tone="sage" dot>Connected</Badge> : <Badge dot>Not connected</Badge>}
        />
        <CardBody>Re-pulls customers, products and orders from Shopify.</CardBody>
        <CardFooter>
          <Button variant="ghost" size="sm" busy={syncing} busyLabel="Syncing…" disabled={!connected} onClick={syncData}>
            Sync data
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader
          title="Webhooks"
          description={webhookStatus === 'connected' ? 'Receiving real-time updates from Shopify.' : 'Real-time order, cart and inventory updates.'}
          aside={webhookStatus === 'connected' ? <Badge tone="sage" dot>Active</Badge> : <Badge dot>Inactive</Badge>}
        />
        <CardBody>
          {webhookStatus === 'connected'
            ? 'Disconnecting stops real-time updates but keeps the store connected. Reconnecting the store re-registers webhooks automatically.'
            : 'Connecting replaces any stale registrations with the full, current webhook set.'}
        </CardBody>
        <CardFooter>
          {webhookStatus === 'connected' ? (
            // Disconnecting is confirmed first (migration.md: disconnects → ConfirmDialog).
            <Button variant="danger" size="sm" busy={webhookBusy} busyLabel="Working…" onClick={() => setConfirmDisconnect(true)}>
              Disconnect webhooks
            </Button>
          ) : (
            <Button size="sm" busy={webhookBusy} busyLabel="Working…" onClick={toggleWebhooks}>
              Connect webhooks
            </Button>
          )}
        </CardFooter>
      </Card>

      <ConfirmDialog
        open={confirmDisconnect}
        title="Disconnect webhooks?"
        description="Real-time order, cart and inventory updates stop until you connect them again. The store stays connected."
        confirmLabel="Disconnect webhooks"
        busy={webhookBusy}
        onCancel={() => setConfirmDisconnect(false)}
        onConfirm={async () => {
          await toggleWebhooks()
          setConfirmDisconnect(false)
        }}
      />
    </Stack>
  )
}

// Tracking script — surface the embed snippet so it can be copied onto the storefront.
function TrackingScriptPanel({ workspace }: { workspace: Workspace | null }) {
  const [script, setScript] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [copied, setCopied] = useState(false)

  // No hardcoded fallback snippet on failure — a made-up script URL would
  // silently break tracking on the storefront. Show an error + retry instead.
  useEffect(() => {
    if (!workspace) return
    let active = true
    setLoading(true)
    setError(false)
    workspaceService
      .generateEmbedScript(workspace.accountId, workspace.id)
      .then((r) => {
        if (!active) return
        const s = (r as { script?: string })?.script || ''
        setScript(s)
        setError(!s)
      })
      .catch(() => { if (active) setError(true) })
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [workspace, attempt])

  const copy = () => {
    navigator.clipboard.writeText(script)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (!workspace) return null

  return (
    <Stack>
      <Hint>
        Paste this before the closing <Code>&lt;/head&gt;</Code> tag on your storefront to feed Marva the
        behavioural stream. On Shopify it&apos;s installed automatically — use this for custom storefronts or to re-add it.
      </Hint>
      <Card tone="recessed" padding="sm">
        {loading ? (
          <LoadingState caption="Loading embed script…" />
        ) : error ? (
          <CardBody>We couldn&apos;t generate your tracking snippet just now.</CardBody>
        ) : (
          <CodeBlock>{script}</CodeBlock>
        )}
      </Card>
      <Inline>
        {error && !loading ? (
          <Button size="sm" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </Button>
        ) : (
          <Button size="sm" onClick={copy} disabled={loading || !script}>
            {copied ? 'Copied' : 'Copy script'}
          </Button>
        )}
      </Inline>
    </Stack>
  )
}
